import { useTranslation } from '@mypoz/i18n';

import { MAX_PACKAGES_PER_ORDER } from '@/features/orders/fixtures.js';
import type { TourId, TourStepView } from '@/features/tour/types.js';
import { useFormat } from '@/lib/format.js';
import { PACKAGES_ROUTE } from '@/shell/navigation.js';

export interface TourStepContext {
  readonly customerId: string;
  /** The customer's name, when the record has one — the welcome greets them by it. */
  readonly name?: string;
}

/**
 * Every tour's steps, in the reader's language (MYP-83).
 *
 * The `data-tour` ids name elements elsewhere: the header and sidebar (`Layout`), the Packages page
 * and its add form, and the order step strip (`StepIndicator`). A step whose element is missing
 * still shows, centred, so renaming one degrades a tour rather than breaking it.
 */
export function useTourSteps(tour: TourId, { customerId, name }: TourStepContext): TourStepView[] {
  const { t } = useTranslation();
  const { t: tNav } = useTranslation('nav');
  const fmt = useFormat();

  switch (tour) {
    case 'welcome': {
      // MYP-136: both pages are in the profile menu in the header, on a laptop and a phone alike.
      const warehouseMenu = tNav('warehouseAddress');
      const vouchersMenu = tNav('coupons');
      return [
        {
          targets: [],
          title: name
            ? t('tour.welcome.intro.titleNamed', { name })
            : t('tour.welcome.intro.title'),
          body: t('tour.welcome.intro.body'),
          nextLabel: t('tour.welcome.intro.start'),
        },
        {
          targets: [{ id: 'header-profile' }],
          title: t('tour.welcome.warehouse.title'),
          body: t('tour.welcome.warehouse.bodyMenu', { menu: warehouseMenu, customerId }),
        },
        {
          targets: [{ id: 'header-notifications' }],
          title: t('tour.welcome.notifications.title'),
          body: t('tour.welcome.notifications.body'),
        },
        {
          targets: [{ id: 'header-profile' }],
          title: t('tour.welcome.profile.title'),
          body: t('tour.welcome.profile.body'),
        },
        {
          targets: [{ id: 'header-wallet' }],
          title: t('tour.welcome.wallet.title'),
          body: t('tour.welcome.wallet.body'),
        },
        {
          targets: [{ id: 'header-profile' }],
          title: t('tour.welcome.vouchers.title'),
          body: t('tour.welcome.vouchers.bodyMenu', { menu: vouchersMenu }),
        },
        {
          targets: [],
          title: t('tour.welcome.done.title'),
          body: t('tour.welcome.done.body'),
          action: { label: t('tour.welcome.done.action'), to: PACKAGES_ROUTE },
        },
      ];
    }

    case 'packages':
      return [
        {
          targets: [{ id: 'packages-add' }],
          title: t('tour.packages.add.title'),
          body: t('tour.packages.add.body'),
        },
        {
          // Only there once the customer has packages; centred until then.
          targets: [{ id: 'packages-tabs' }],
          title: t('tour.packages.tabs.title'),
          body: t('tour.packages.tabs.body'),
        },
        {
          targets: [{ id: 'packages-list' }, { id: 'packages-empty' }],
          title: t('tour.packages.list.title'),
          body: t('tour.packages.list.body'),
        },
      ];

    case 'add_package':
      return [
        {
          targets: [{ id: 'package-form-tracking' }],
          title: t('tour.addPackage.tracking.title'),
          body: t('tour.addPackage.tracking.body'),
        },
        {
          targets: [{ id: 'package-form-warehouse' }],
          title: t('tour.addPackage.warehouse.title'),
          body: t('tour.addPackage.warehouse.body'),
        },
        {
          targets: [{ id: 'package-form-item' }],
          title: t('tour.addPackage.item.title'),
          body: t('tour.addPackage.item.body'),
        },
        {
          targets: [{ id: 'package-form-save' }],
          title: t('tour.addPackage.save.title'),
          body: t('tour.addPackage.save.body'),
        },
      ];

    case 'create_order': {
      const max = fmt.number(MAX_PACKAGES_PER_ORDER);
      return [
        {
          targets: [{ id: 'order-step-0' }],
          title: t('tour.createOrder.packages.title'),
          body: t('tour.createOrder.packages.body', { max }),
        },
        {
          targets: [{ id: 'order-step-1' }],
          title: t('tour.createOrder.channel.title'),
          body: t('tour.createOrder.channel.body'),
        },
        {
          targets: [{ id: 'order-step-2' }],
          title: t('tour.createOrder.delivery.title'),
          body: t('tour.createOrder.delivery.body'),
        },
        {
          targets: [{ id: 'order-step-3' }],
          title: t('tour.createOrder.confirm.title'),
          body: t('tour.createOrder.confirm.body'),
        },
      ];
    }
  }
}
